import type { ViewMode } from '@components'
import { type RefObject, useEffect, useRef, useState } from 'react'

type UseCrossfadeViewModeReturn<T extends HTMLElement> = {
  displayedMode: ViewMode
  isFading: boolean
  ref: RefObject<T | null>
}

export const useCrossfadeViewMode = <T extends HTMLElement = HTMLDivElement>(
  mode: ViewMode
): UseCrossfadeViewModeReturn<T> => {
  const [displayedMode, setDisplayedMode] = useState<ViewMode>(mode)
  const [isFading, setIsFading] = useState(false)
  const ref = useRef<T | null>(null)

  useEffect(() => {
    if (mode === displayedMode) return

    setIsFading(true)

    const el = ref.current
    if (!el) {
      setDisplayedMode(mode)
      return
    }

    const handler = (e: TransitionEvent) => {
      if (e.target !== el || e.propertyName !== 'opacity') return
      setDisplayedMode(mode)
    }
    el.addEventListener('transitionend', handler, { once: true })
    return () => el.removeEventListener('transitionend', handler)
  }, [mode, displayedMode])

  useEffect(() => {
    if (displayedMode === mode && isFading) setIsFading(false)
  }, [displayedMode, mode, isFading])

  return { displayedMode, isFading, ref }
}
